import type { IndicatorScaleMode } from '@app-types/investmentIndicators';

export interface IndicatorBarGeometry {
  // Fractions of the full plot width, independent of the label's size.
  origin: number;
  endpoint: number;
  left: number;
  width: number;
  direction: 1 | -1;
}

export interface IndicatorBarLabelLayout {
  mode: 'outside' | 'inside' | 'backed' | 'missing' | 'line';
  left: number;
}

const clamp = (value: number, min = 0, max = 1): number =>
  Math.min(Math.max(value, min), max);

export const getIndicatorBarGeometry = (
  scaleMode: IndicatorScaleMode,
  value: number | null,
  min: number,
  max: number,
): IndicatorBarGeometry | null => {
  if (value === null || !Number.isFinite(value) || !Number.isFinite(min) ||
      !Number.isFinite(max) || max <= min) return null;

  const origin = scaleMode === 'centered-zero' ? clamp(-min / (max - min)) : 0;
  const endpoint = clamp((value - min) / (max - min));
  return {
    origin,
    endpoint,
    left: Math.min(origin, endpoint),
    width: Math.abs(endpoint - origin),
    direction: endpoint < origin ? -1 : 1,
  };
};

// Keep this inset in sync with @space-xs in the card's Less rules.
const LABEL_INSET = 4;

export const getIndicatorBarLabelLayout = (
  geometry: IndicatorBarGeometry | null,
  trackWidth: number,
  labelWidth: number,
): IndicatorBarLabelLayout | null => {
  if (!Number.isFinite(trackWidth) || trackWidth <= 0 ||
      !Number.isFinite(labelWidth) || labelWidth <= 0) return null;

  const inset = LABEL_INSET;
  if (labelWidth > trackWidth - 2 * inset) return { mode: 'line', left: inset };
  if (!geometry) return { mode: 'missing', left: trackWidth - inset - labelWidth };

  const endpoint = geometry.endpoint * trackWidth;
  const outsideLeft = geometry.direction === 1
    ? endpoint + inset
    : endpoint - inset - labelWidth;
  if (outsideLeft >= inset && outsideLeft + labelWidth <= trackWidth - inset) {
    return { mode: 'outside', left: outsideLeft };
  }

  const insideLeft = geometry.direction === 1
    ? endpoint - inset - labelWidth
    : endpoint + inset;
  const left = clamp(insideLeft, inset, trackWidth - inset - labelWidth);
  const fillLeft = geometry.left * trackWidth;
  const fillRight = (geometry.left + geometry.width) * trackWidth;
  const insideFill = geometry.width > 0 && left >= fillLeft && left + labelWidth <= fillRight;
  return { mode: insideFill ? 'inside' : 'backed', left };
};
